import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { numberToWords } from "../Inovice/utils";
import { formatNumberWithCommas } from "../../utils";

export function MilestonePdfGeneratorDunes(data, milestoneData = null, fileName = "tax_invoice.pdf") {
  const placeholder = "N/A";
  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  // Constants for layout
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const footerReserve = 55;

  // Fixed header dimensions
  const logoHeight = 25;
  const logoTopMargin = margin;
  const billToBoxTop = logoTopMargin + logoHeight + 5;
  const trnBoxHeight = 10;
  let billToBoxHeight = 50;
  let trnBoxTop = billToBoxTop + billToBoxHeight;
  let headerEndY = trnBoxTop + trnBoxHeight + 3;

  // Helper function to wrap text
  const wrapText = (text, width) => doc.splitTextToSize(text || placeholder, width);

  // ==================== HEADER SECTION (on every page) ====================
  const drawHeader = () => {
    const colWidth = contentWidth / 3;
    const rowHeight = 4;

    // Logo
    doc.addImage(
      "/DUNES_LOGO.png",
      "PNG",
      margin,
      logoTopMargin,
      80,
      logoHeight
    );

    // TAX INVOICE title - right aligned
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("TAX INVOICE", pageWidth - margin, logoTopMargin + 15, {
      align: "right",
    });

    const billToLines = [
      data?.billTo?.num || placeholder,
      data?.billTo?.name || placeholder,
      data?.billTo?.city || placeholder,
      `PO BOX: ${data?.billTo?.postal || placeholder}`,
      data?.billTo?.street || placeholder,
      // data?.billTo?.address || placeholder,
    ];
    const shipToLines = [
      data?.shipTo?.num || placeholder,
      data?.shipTo?.name || placeholder,
      data?.shipTo?.city || placeholder,
      `PO BOX: ${data?.shipTo?.postal || placeholder}`,
      data?.shipTo?.street || placeholder,
      // data?.shipTo?.address || placeholder,
    ];
    const invoiceDetails = [
      { label: "INVOICE NO", value: data?.invoiceNumber?.[0] || placeholder },
      { label: "DATE", value: data?.invoiceDate || placeholder },
      { label: "SALES REP", value: data?.salesRep || placeholder },
      { label: "PAYMENT", value: data?.paymentTerms || placeholder },
      { label: "PO REF NO", value: data?.poRefNo || placeholder },
      { label: "DELIVERY NO", value: data?.deliveryNo || placeholder },
    ];

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    const countWrappedLines = (lines, width) =>
      lines.reduce((sum, line) => sum + wrapText(line, width).length, 0);

    const boxPaddingTop = 4;
    const boxPaddingBottom = 1;
    const billToLineCount = 1 + countWrappedLines(billToLines, colWidth - 6);
    const shipToLineCount = 1 + countWrappedLines(shipToLines, colWidth - 6);
    const invoiceLineCount = invoiceDetails.length;
    const maxLineCount = Math.max(billToLineCount, shipToLineCount, invoiceLineCount);

    billToBoxHeight = boxPaddingTop + boxPaddingBottom + maxLineCount * rowHeight;
    trnBoxTop = billToBoxTop + billToBoxHeight;
    headerEndY = trnBoxTop + trnBoxHeight + 3;

    // Bill To / Ship To / Invoice Details Box
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, billToBoxTop, contentWidth, billToBoxHeight);

    // Draw vertical dividers
    doc.line(margin + colWidth, billToBoxTop, margin + colWidth, billToBoxTop + billToBoxHeight);
    doc.line(margin + colWidth * 2, billToBoxTop, margin + colWidth * 2, billToBoxTop + billToBoxHeight);

    // BILL TO Section
    let y1 = billToBoxTop + boxPaddingTop;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("BILL TO:", margin + 3, y1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    y1 += rowHeight;

    billToLines.forEach((line) => {
      const lines = wrapText(line, colWidth - 6);
      lines.forEach((wrappedLine) => {
        doc.text(wrappedLine, margin + 3, y1);
        y1 += rowHeight;
      });
    });

    // SHIP TO Section
    let y2 = billToBoxTop + boxPaddingTop;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("SHIP TO:", margin + colWidth + 3, y2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    y2 += rowHeight;

    shipToLines.forEach((line) => {
      const lines = wrapText(line, colWidth - 6);
      lines.forEach((wrappedLine) => {
        doc.text(wrappedLine, margin + colWidth + 3, y2);
        y2 += rowHeight;
      });
    });

    // Invoice Details Section (right column) - Two column layout
    let y3 = billToBoxTop + boxPaddingTop;
    const labelX = margin + colWidth * 2 + 3;
    const valueX = margin + colWidth * 2 + 30;
    doc.setFontSize(7);

    invoiceDetails.forEach((detail) => {
      doc.setFont("helvetica", "bold");
      doc.text(detail.label, labelX, y3);
      doc.setFont("helvetica", "normal");
      const wrappedValue = wrapText(detail.value, colWidth - 35);
      doc.text(wrappedValue[0] || placeholder, valueX, y3);
      y3 += rowHeight;
    });

    // TRN Section
    doc.rect(margin, trnBoxTop, contentWidth, trnBoxHeight);
    doc.line(margin + colWidth, trnBoxTop, margin + colWidth, trnBoxTop + trnBoxHeight);
    doc.line(margin + colWidth * 2, trnBoxTop, margin + colWidth * 2, trnBoxTop + trnBoxHeight);

    const trnY = trnBoxTop + 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(`TRN: ${data?.billTo?.trn || placeholder}`, margin + 3, trnY);
    doc.text(`TRN: ${data?.shipTo?.trn || placeholder}`, margin + colWidth + 3, trnY);
    doc.text(`TRIGON TRN: ${data?.trigonTRN || placeholder}`, margin + colWidth * 2 + 3, trnY);

    return headerEndY;
  };

  // ==================== FOOTER SECTION (on every page) ====================
  const drawFooter = (pageNum, totalPages) => {
    const footerLineY = pageHeight - 38;
    const authorizedY = footerLineY - 2;
    const footerTextStartY = footerLineY + 4;
    const footerLineHeight = 3.5;
    const certifiedY = authorizedY - 8;
    const forCompanyY = authorizedY - 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(
      "Certified that the particulars given above are true and correct",
      margin,
      certifiedY
    );
    doc.text("FOR DUNES TECHNOLOGIES LLC", margin, forCompanyY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("AUTHORIZED SIGNATORY", margin, authorizedY);

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, footerLineY, pageWidth - margin, footerLineY);

    doc.setFontSize(6);
    doc.text(
      "Bank Details: DUNES TECHNOLOGIES LLC, Emirates NBD, Dubai, UAE.",
      pageWidth / 2,
      footerTextStartY,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    const bankDetails = [
      "Dirham Account No.: 101 5855556 401, Dirham IBAN No.: AE820260001015855556401",
      "Dollar Account No.: 102 5855556 402, Dollar IBAN No.: AE900260001025855556402",
      "Swift Code: EBILAEAD",
      "Routing Code: 302620122",
    ];

    bankDetails.forEach((line, index) => {
      doc.text(
        line,
        pageWidth / 2,
        footerTextStartY + (index + 1) * footerLineHeight,
        { align: "center" }
      );
    });

    doc.setFont("helvetica", "bold");
    doc.text(
      "Payment to be made by crossed cheques in favour of DUNES TECHNOLOGIES LLC",
      pageWidth / 2,
      footerTextStartY + (bankDetails.length + 1) * footerLineHeight,
      { align: "center" }
    );

    doc.setFont("helvetica", "bold");
    doc.text(
      "Website: www.dunestech.ae",
      pageWidth / 2,
      footerTextStartY + (bankDetails.length + 2) * footerLineHeight,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Page No:${pageNum}`, margin, pageHeight - 8);
  };

  // ==================== ITEMS TABLE ====================
  const drawItemsTable = (startY) => {
    const headers = [
      "S.NO",
      "ITEM",
      "DESCRIPTION",
      "QTY",
      "RATE",
      "DISC",
      "Price After\nDiscount",
      "VAT\nRate%",
      "VAT\nAmount",
      "Total\nAmount",
    ];

    const rows = data?.invoiceItems?.map((item, index) => [
      index + 1,
      item.itemCode || placeholder,
      item.description || placeholder,
      item.quantity || placeholder,
      formatNumberWithCommas(item.rate) || placeholder,
      item.discount || placeholder,
      formatNumberWithCommas(item.priceAfterDiscount) || placeholder,
      item.vatPercentage || placeholder,
      formatNumberWithCommas(item.vatAmount) || placeholder,
      formatNumberWithCommas(item.totalAmount) || placeholder,
    ]) || [];

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: startY,
      margin: {
        top: headerEndY,
        left: margin,
        right: margin,
        bottom: footerReserve,
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 24 },
        2: { cellWidth: 36 },
        3: { cellWidth: 12, halign: "center" },
        4: { cellWidth: 16, halign: "right" },
        5: { cellWidth: 12, halign: "center" },
        6: { cellWidth: 20, halign: "right" },
        7: { cellWidth: 12, halign: "center" },
        8: { cellWidth: 16, halign: "right" },
        9: { cellWidth: 22, halign: "right" },
      },
      didDrawPage: function () {
        drawHeader();
      },
      showHead: "everyPage",
    });

    return doc.lastAutoTable.finalY;
  };

  const drawMilestoneSection = (startY) => {
    const lineHeight = 4;
    const rowSpacing = 1;
    const rowHeight = lineHeight + rowSpacing;
    const sectionWidth = doc.internal.pageSize.getWidth() - margin * 2 - 1;

    const leftColWidth = sectionWidth * 0.4;
    const rightColWidth = sectionWidth * 0.6;

    const totalBeforeTax = parseFloat(data.totals.totalBeforeTax) || 0;
    const totalVatAmount = parseFloat(data.totals.totalVatAfterRetention) || 0;
    const milestonePercentage = parseFloat(milestoneData.percentage) || 0;
    const retentionPercentage = parseFloat(data.retention) || 0;
    const advancePercentage = parseFloat(data.advance) || 0;

    const milestoneAmount = (totalBeforeTax * milestonePercentage / 100).toFixed(2);
    const milestoneVatAmount = (totalVatAmount * milestonePercentage / 100).toFixed(2);
    const retentionAmount = (milestoneAmount * retentionPercentage / 100).toFixed(2);
    const advanceReceived = (milestoneAmount * advancePercentage / 100).toFixed(2);
    const balanceAfterRetentionBeforeTax = (milestoneAmount - retentionAmount).toFixed(2);
    const balanceAfterRetentionBeforeTaxAndAdvance = (balanceAfterRetentionBeforeTax - advanceReceived).toFixed(2);
    const milestoneVatPercentage = (milestoneAmount > 0) ? (milestoneVatAmount / milestoneAmount) * 100 : 0;
    const vatOnRetention = (milestoneVatPercentage / 100) * retentionAmount;
    const vatOnAdvance = (milestoneVatPercentage / 100) * advanceReceived;
    const totalVatAmountAfterRetention = (milestoneVatAmount - vatOnRetention - vatOnAdvance).toFixed(2);
    const balanceTotal = (parseFloat(balanceAfterRetentionBeforeTaxAndAdvance) + parseFloat(totalVatAmountAfterRetention)).toFixed(2);

    const headerBoxHeight = 40;

    const amountWords = "AED " + numberToWords(balanceTotal);
    const splitWords = doc.splitTextToSize(amountWords, leftColWidth - 20);

    const percentageTextHeight = 30;
    const amountWordsHeight = Math.max(24, splitWords.length * (rowHeight + 1) + 10);
    const dividerHeight = 4;
    const leftColHeight = percentageTextHeight + dividerHeight + amountWordsHeight;

    const financials = [
      ["Total Amount Before Tax:", totalBeforeTax],
      [`${milestoneData.name} (${milestonePercentage}%):`, milestoneAmount],
      [`Retention Amount (${retentionPercentage}%):`, retentionAmount],
      ["Balance After Retention before VAT:", balanceAfterRetentionBeforeTax],
      [`Advance Amount (${advancePercentage}%):`, advanceReceived],
      ["Balance After Retention/Advance before VAT:", balanceAfterRetentionBeforeTaxAndAdvance],
      ["VAT Amount after Retention:", totalVatAmountAfterRetention],
      ["Balance Amount After Tax:", balanceTotal],
    ];

    let rightColHeight = 0;
    const rowPadding = 1;

    const labelWidth = rightColWidth * 0.7 - 15;
    const financialsWithHeights = financials.map(row => {
      const labelLines = doc.splitTextToSize(row[0], labelWidth);
      const lineCount = labelLines.length;
      const contentHeight = lineCount * rowHeight + (lineCount > 1 ? (lineCount - 1) * rowSpacing : 0);
      const rowBoxHeight = contentHeight + rowPadding * 2;

      return {
        label: row[0],
        value: row[1],
        lines: labelLines,
        contentHeight: contentHeight,
        rowBoxHeight: rowBoxHeight
      };
    });

    financialsWithHeights.forEach(item => {
      rightColHeight += item.rowBoxHeight;
    });

    const tableHeight = Math.max(leftColHeight, rightColHeight);
    const requiredHeight = headerBoxHeight + tableHeight + 10;

    let sectionStartY = startY + 10;
    if (sectionStartY + requiredHeight > pageHeight - footerReserve) {
      doc.addPage();
      drawHeader();
      sectionStartY = headerEndY + 10;
    }

    let localY = sectionStartY;

    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.rect(margin, localY, sectionWidth, headerBoxHeight);

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("MILESTONE DETAILS", margin + sectionWidth / 2, localY + 12, { align: "center" });
    doc.setFontSize(7);

    const headerItemX1 = margin + 15;
    const headerValueX1 = margin + 60;

    doc.setFont("helvetica", "bold");
    doc.text("MILESTONE NAME:", headerItemX1, localY + 22);
    doc.setFont("helvetica", "normal");

    const milestoneName = milestoneData.name || "-";
    const maxNameWidth = sectionWidth - 65;
    const milestoneNameLines = doc.splitTextToSize(milestoneName, maxNameWidth);
    doc.text(milestoneNameLines, headerValueX1, localY + 22);

    let nameOffset = (milestoneNameLines.length - 1) * (rowHeight + 2);

    doc.setFont("helvetica", "bold");
    doc.text("INVOICE NO:", headerItemX1, localY + 30 + nameOffset);
    doc.setFont("helvetica", "normal");
    doc.text(data.invoiceNumber[0] || "-", headerValueX1, localY + 30 + nameOffset);

    doc.setFont("helvetica", "bold");
    doc.text("DATE:", headerItemX1, localY + 38 + nameOffset);
    doc.setFont("helvetica", "normal");
    doc.text(data.invoiceDate || "-", headerValueX1, localY + 38 + nameOffset);

    localY += headerBoxHeight;

    const leftColY = localY;
    const leftColX = margin;
    const rightColX = margin + leftColWidth;

    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.rect(leftColX, leftColY, leftColWidth, tableHeight);

    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    const infoStartY = leftColY + 8;
    const infoLineHeight = 6;
    doc.text("MILESTONE INFORMATION:", leftColX + 6, infoStartY);
    doc.setFont("helvetica", "normal");
    doc.text(`Milestone Percentage: ${milestonePercentage}%`, leftColX + 8, infoStartY + infoLineHeight);
    doc.text(`Retention Percentage: ${retentionPercentage}%`, leftColX + 8, infoStartY + infoLineHeight * 2);
    doc.text(`Advance Percentage: ${advancePercentage}%`, leftColX + 8, infoStartY + infoLineHeight * 3);

    const dividerY = leftColY + percentageTextHeight;
    doc.line(leftColX, dividerY, leftColX + leftColWidth, dividerY);

    doc.setFont("helvetica", "bold");
    doc.text("AMOUNT IN WORDS:", leftColX + 6, dividerY + 8);
    doc.setFont("helvetica", "normal");
    doc.text(splitWords, leftColX + 8, dividerY + 14);

    // Right column table
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.rect(rightColX, leftColY, rightColWidth, tableHeight);

    const valueDividerX = rightColX + rightColWidth * 0.68;
    doc.line(valueDividerX, leftColY, valueDividerX, leftColY + tableHeight);

    let currentRowY = leftColY;
    const paddingX = 6;
    const paddingY = 4;

    financialsWithHeights.forEach((item, index) => {
      doc.setFont("helvetica", "bold");
      let labelY = currentRowY + paddingY;
      item.lines.forEach((line, i) => {
        doc.text(line, rightColX + paddingX, labelY + (i * rowHeight));
      });

      doc.setFont("helvetica", "normal");
      const valueY = currentRowY + (item.rowBoxHeight / 2) + 1;
      doc.text(formatNumberWithCommas(item.value), rightColX + rightColWidth - paddingX, valueY, {
        align: "right",
      });

      const rowBottomY = currentRowY + item.rowBoxHeight;
      if (index < financialsWithHeights.length - 1) {
        doc.line(rightColX, rowBottomY, rightColX + rightColWidth, rowBottomY);
      }

      currentRowY = rowBottomY;
    });

    const finalY = leftColY + tableHeight + 10;

    return finalY;
  };

  // ==================== GENERATE PDF ====================
  const tableStartY = drawHeader();
  const tableEndY = drawItemsTable(tableStartY);

  let contentEndY = tableEndY;
  if (milestoneData) {
    contentEndY = drawMilestoneSection(tableEndY);
  }

  let descriptionY = contentEndY + 5;
  const descriptionHeight = 8;
  if (descriptionY + descriptionHeight > pageHeight - footerReserve) {
    doc.addPage();
    drawHeader();
    descriptionY = headerEndY + 5;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(`Description: ${data.description || placeholder}`, margin, descriptionY);

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  // Save the PDF
  doc.save(fileName);
}
